import { Stack, Spacer } from 'common/ui/Layout';
import Text from 'common/ui/Text';
import { css } from 'styled-components';
import IconButton from '@mui/material/IconButton';
import { Colors } from 'styles';
import { forwardRef } from 'react';
import { rootTheme } from 'theme/rootTheme';

type AccessoryButtonProps = {
  icon: React.ReactNode;
  onClick?: (arg1: React.SyntheticEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  'data-testid'?: string;
};

export const AccessoryButton = forwardRef<HTMLButtonElement, AccessoryButtonProps>(
  ({ icon, onClick, ...props }, ref): React.ReactElement => {
    return (
      <IconButton
        {...props}
        ref={ref}
        size="small"
        css={css`
          color: ${Colors.gray7};
          &:hover,
          &:focus {
            color: ${Colors.gray10};
          }
          .MuiSvgIcon-root {
            font-size: inherit;
          }
        `}
        onClick={onClick}
      >
        {icon}
      </IconButton>
    );
  }
);
AccessoryButton.displayName = 'AccessoryButton';

type BaseWidgetProps = {
  style?: {
    [key: string]: number | string;
  };
  className?: string;
  placeholder: string;
  text: Nullable<React.ReactNode>;
  accessories?: React.ReactNode;
  onClick?: (arg1: React.SyntheticEvent<HTMLDivElement>) => void;
  disabled?: boolean;
  ['data-testid']?: string;
};

export const BaseWidget = forwardRef<HTMLDivElement, BaseWidgetProps>(
  (
    {
      style,
      className,
      placeholder,
      text,
      accessories,
      onClick,
      disabled,
      'data-testid': dataTestid,
    },
    ref
  ) => {
    const hasText = text != null;

    const containerStyle = {
      backgroundColor: Colors.gray4,
      borderRadius: rootTheme.radius.xs,
      paddingLeft: 8,
      paddingRight: 8,
      borderWidth: 1,
      borderColor: hasText ? Colors.gray6 : 'transparent',
      borderStyle: 'solid',
      cursor: disabled ? 'default' : onClick != null ? 'pointer' : 'default',
      opacity: disabled ? 0.5 : 1,
      pointerEvents: disabled ? 'none' : 'auto',
    } as const;

    return (
      // @ts-expect-error styled-components typing issue: Stack component doesn't properly infer
      // types when combining standard HTML attributes (style, ref, className, onClick) with custom
      // styled props (alignY). This is a known limitation of styled-components type system.
      <Stack
        css={containerStyle}
        style={style}
        alignY="center"
        className={className}
        ref={ref}
        role="button"
        tabIndex={-1}
        aria-disabled={disabled}
        onClick={onClick}
        data-testid={dataTestid}
      >
        <Text color={hasText ? 'primary' : 'secondary'} overflow="ellipsis" data-testid="name">
          {text ?? placeholder}
        </Text>
        <Spacer />
        {accessories != null && <Stack css="margin-right: -0.3em;">{accessories}</Stack>}
      </Stack>
    );
  }
);

BaseWidget.displayName = 'BaseWidget';
